'use client';

import type { GetProject } from '@/server/api/rest/project';
import { useLocalePath } from '@/shared/hooks';
import Image from 'next/image';
import Link from 'next/link';
import type { FC } from 'react';

type CaseStudyCardProps = {
  readonly project: NonNullable<GetProject>;
  readonly images: GetProject['images'];
  readonly index: number;
  readonly slug: GetProject['slug'];
  readonly isHero: boolean;
};

export const CaseStudyCard: FC<CaseStudyCardProps> = ({ images, project, slug, isHero }) => {
  const { to } = useLocalePath();
  const defaultImg = '/images/tend.png';
  const src = images[0]?.url ?? images[1]?.url ?? defaultImg;

  return (
    <Link
      href={to(`/project/${slug}`)}
      className="group relative block overflow-hidden rounded-[12px] bg-white/5 aspect-[16/7]"
    >
      <Image
        src={src}
        alt={project.title}
        fill
        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        sizes="(max-width: 768px) 100vw, 50vw"
      />
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      {/* Category tag */}
      {project.client && (
        <span className="absolute top-4 right-4 text-[#F24E1E] text-xs font-medium tracking-widest uppercase">
          {project.client.name}
        </span>
      )}
      {/* Project title */}
      <h3
        className="absolute bottom-6 left-6 font-medium text-white leading-none text-[clamp(24px,3vw,40px)]"
      >
        {project.title}
      </h3>
      {/* Hover reveal strip */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-4 border-t border-white/10 bg-black/60 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
        <span className="text-white/80 text-sm">{project.subtitle}</span>
        <span className="text-[#F24E1E]">→</span>
      </div>
    </Link>
  );
};
